import { useEffect, useState } from 'react';
import { useTranslation } from '@packages/utils';
import EditableInput from 'components/Base/EditableFields/EditableInput';
import {
  useShowToastNotification,
  ENotificationType,
} from 'components/Base/Notification';
import { useEditEventAttendanceMutation } from 'redux/apiSlice/events.slice';
import { TRtkErrorType } from 'data/types/generic.types';
import useGetValidationData from 'components/Base/Form/validationRules';
import { Flex } from 'antd';
import { HelpOutline, PeopleGroupOutline } from 'assets/icon/icon';
import { Text } from 'components/Base/Typography';
import { Tooltip } from 'components/Base/Tooltip';
import { TEventAttendanceProps } from './EventAttendance.types';

const EventAttendance = ({
  eventId,
  attendees,
  requiresRegistration = false,
}: TEventAttendanceProps) => {
  const { EValidationMessages, validationRules } = useGetValidationData();
  const { t } = useTranslation({
    keyPrefix:
      'descriptions.admin-portal.events.details.basic-info.event-attendance',
  });

  const [attendanceCount, setAttendanceCount] = useState<string>(
    attendees ? String(attendees) : '0',
  );
  // For registration-required events the count is calculated by the backend
  // from the attendance roster (GROW-4289), so keep the shown value in sync
  // with whatever the latest event fetch returned.
  useEffect(() => {
    setAttendanceCount(attendees ? String(attendees) : '0');
  }, [attendees]);
  const { showToastNotification } = useShowToastNotification();
  const [editEventAttendance, { isLoading }] = useEditEventAttendanceMutation();
  const [errorMessage, setErrorMessage] = useState('');

  const validateAttendance = async (newValue: string) => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) {
      return Promise.reject(new Error(EValidationMessages.DECREASE_IMPOSSIBLE));
    }

    const numericValue = Number(trimmedValue);
    if (numericValue < 0 || Number.isNaN(numericValue)) {
      return Promise.reject(new Error(EValidationMessages.DECREASE_IMPOSSIBLE));
    }
    return Promise.resolve();
  };

  const onEdit = async (val: string) => {
    try {
      await validateAttendance(val);
      await editEventAttendance({
        eventId,
        attendees: Number(val),
      }).unwrap();
      setAttendanceCount(val);
      showToastNotification({
        message: t('SUCCESS_MESSAGE', 'Attendance updated successfully'),
        type: ENotificationType.SUCCESS,
      });
      setErrorMessage('');
    } catch (e) {
      const apiErrorMessage =
        (e as TRtkErrorType)?.data?.message || (e as Error).message;
      setErrorMessage(apiErrorMessage);
      showToastNotification({
        message:
          apiErrorMessage ||
          'Error updating attendance. Please try again later.',
        type: ENotificationType.ERROR,
      });
    }
  };

  return (
    <Flex align="center" data-cy="event-attendance-input">
      <Flex align="center" gap={8} style={{ width: 160 }}>
        <PeopleGroupOutline size={24} />
        <Text color="#808080">Attended</Text>
        <Tooltip
          title={
            requiresRegistration
              ? t(
                  'READONLY_TOOLTIP',
                  'Calculated automatically from registrants marked as attended in the Attendance tab',
                )
              : t(
                  'TOOLTIP',
                  'Enter the number of people who attended the event',
                )
          }
        >
          <div data-testid="event-attendance-help">
            <HelpOutline size={16} />
          </div>
        </Tooltip>
      </Flex>
      <EditableInput
        onEdit={onEdit}
        title={attendanceCount}
        fitContent
        showValue={attendanceCount}
        errorMessage={errorMessage}
        isLoading={isLoading}
        canRemove={false}
        rules={[validationRules.positiveNumbers()]}
        backIcon
        readonly={requiresRegistration}
      />
    </Flex>
  );
};

export default EventAttendance;
